/**
 * ReviewFieldsPanel
 *
 * BPO-modal-style form fields panel for the claims review step.
 * Matches the exact UI of BPOReviewModal with editing, source badges,
 * and AI suggestion indicators.
 */
import {
  AlertTriangle,
  Car,
  Check,
  ChevronRight,
  Edit3,
  ExternalLink,
  Plus,
  Shield,
  Sparkles,
  User,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { US_STATE_OPTIONS } from '@/components/ui/address-autocomplete/constants';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import {
  useAssignVehiclesToCoverageMutation,
  useGetBpoContextQuery,
  useGetCoverageQuery,
  useUpdateBpoContextMutation,
} from '@/services/api/coverageService';
import {
  type CreateVehicle,
  type UpdateVehicle,
  useVehiclesCreateMutation,
  useVehiclesUpdateMutation,
  type VehicleType,
} from '@/services/api/subjectsService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import type {
  CitedField,
  CoverageSchema,
  CoverageVehicleSummary,
  OpenClaimCitedField,
  OpenClaimContextSchema,
  ReadVehicle,
  SourceReference,
  UpdateBPOContextRequest,
} from '@/services/types/coverage-types';
import { CoverageCategory, SourceType } from '@/services/types/coverage-types';

/** Source info for a specific field */
export interface FieldSourceInfo {
  artifactId: string;
  citationIds: string[];
  sourceType: SourceType;
  sourceLabel: string;
  isAiSuggestion: boolean;
  isWrong?: boolean; // Pre-populated value was determined to be incorrect
  wrongReason?: string; // Explanation of why the value was wrong
}

interface ReviewFieldsPanelProps {
  matterId: string;
  coverageId: string;
  coverage?: CoverageSchema;
  activeFieldKey: string | null;
  /** When set, the field is "locked" and hover won't change activeFieldKey */
  lockedFieldKey?: string | null;
  onActiveFieldKeyChange: (key: string | null) => void;
  /** Handle explicit field clicks (for locking/unlocking) */
  onFieldClick?: (key: string) => void;
  onActiveFieldSourceChange: (source: FieldSourceInfo | null) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

function mapSourceTypeToArtifactType(sourceType: SourceType): ArtifactType {
  switch (sourceType) {
    case SourceType.POLICE_REPORT:
      return ArtifactType.DOCUMENT;
    case SourceType.INTAKE:
      return ArtifactType.INTAKE_NOTE;
    case SourceType.COVERAGE_DOC:
      return ArtifactType.DOCUMENT;
    default:
      return ArtifactType.DOCUMENT;
  }
}

function openClaimFieldToSource<T>(
  field: OpenClaimCitedField<T> | undefined,
): SourceReference | null {
  if (!field?.source_artifact_id) return null;
  return {
    source_type: SourceType.COVERAGE_DOC,
    source_label: 'AI Suggested',
    artifact_id: field.source_artifact_id,
    blob_id: null,
  };
}

function getMergedValue<T>(
  openClaimField: OpenClaimCitedField<T> | undefined,
  bpoField: CitedField<T | null> | null | undefined,
): {
  value: T | null;
  source: SourceReference | null;
  isAiSuggestion: boolean;
  isWrong?: boolean;
  wrongReason?: string;
} {
  // Check if AI marked this field as WRONG (pre-populated value was incorrect)
  if (openClaimField?.status === 'WRONG') {
    return {
      value: null, // Don't use the value - it was marked wrong
      source: openClaimFieldToSource(openClaimField),
      isAiSuggestion: true,
      isWrong: true,
      wrongReason:
        openClaimField.reasoning ?? 'Pre-populated value was incorrect',
    };
  }
  if (openClaimField?.value != null && openClaimField.value !== '') {
    return {
      value: openClaimField.value,
      source: openClaimFieldToSource(openClaimField),
      isAiSuggestion: true,
    };
  }
  return {
    value: bpoField?.value ?? null,
    source: bpoField?.source ?? null,
    isAiSuggestion: false,
  };
}

function getMergedStringValue(
  openClaimField: OpenClaimCitedField<string> | undefined,
  directValue: string | null | undefined,
): {
  value: string | null;
  source: SourceReference | null;
  isAiSuggestion: boolean;
  isWrong?: boolean;
  wrongReason?: string;
} {
  // Check if AI marked this field as WRONG (pre-populated value was incorrect)
  if (openClaimField?.status === 'WRONG') {
    return {
      value: null, // Don't use the value - it was marked wrong
      source: openClaimFieldToSource(openClaimField),
      isAiSuggestion: true,
      isWrong: true,
      wrongReason:
        openClaimField.reasoning ?? 'Pre-populated value was incorrect',
    };
  }
  if (openClaimField?.value != null && openClaimField.value !== '') {
    return {
      value: openClaimField.value,
      source: openClaimFieldToSource(openClaimField),
      isAiSuggestion: true,
    };
  }
  return { value: directValue ?? null, source: null, isAiSuggestion: false };
}

// =============================================================================
// Source Badge Component
// =============================================================================

interface SourceBadgeProps {
  source: SourceReference | null | undefined;
}

const SourceBadge: React.FC<SourceBadgeProps> = ({ source }) => {
  if (!source) return null;

  if (source.artifact_id) {
    return (
      <InspectArtifactButton
        artifactId={source.artifact_id}
        artifactType={mapSourceTypeToArtifactType(source.source_type)}
        buttonClassName="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors"
        icon={<ExternalLink size={10} />}
        label={source.source_label?.split(' ')[0] || 'Source'}
      />
    );
  }

  if (source.source_label) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-500 rounded">
        {source.source_label.split(' ')[0]}
      </span>
    );
  }

  return null;
};

// =============================================================================
// Editable Field Component
// =============================================================================

interface EditableFieldProps {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
  multiline?: boolean;
  source?: SourceReference | null;
  isAiSuggestion?: boolean;
  isWrong?: boolean;
  wrongReason?: string;
  fieldKey: string;
  isActive: boolean;
  /** Whether this field is "locked" (sources pane frozen to it) */
  isLocked?: boolean;
  onHover: (key: string | null) => void;
  onClick: (key: string) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  placeholder = 'Not set',
  onSave,
  multiline,
  source,
  isAiSuggestion = false,
  isWrong = false,
  wrongReason,
  fieldKey,
  isActive,
  isLocked = false,
  onHover,
  onClick,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value || '');
    }
  }, [value, isEditing]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setIsEditing(false);
    } catch (e: unknown) {
      let errorMessage = 'Failed to save';
      if (e && typeof e === 'object') {
        const rtkError = e as { data?: { error?: string }; message?: string };
        if (rtkError.data?.error) {
          errorMessage = rtkError.data.error;
        } else if (rtkError.message) {
          errorMessage = rtkError.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value || '');
    setError(null);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className="group"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => onHover(fieldKey)}
        onMouseLeave={() => onHover(null)}
      >
        <span className="block text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-1.5">
          {label}
        </span>
        {multiline ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-zinc-400 resize-none transition-all"
            rows={3}
            placeholder={placeholder}
            disabled={isSaving}
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-zinc-400 transition-all"
            placeholder={placeholder}
            disabled={isSaving}
          />
        )}
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="px-3 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`group cursor-pointer text-left w-full rounded-lg p-2 transition-all ${
        isLocked
          ? 'bg-indigo-100 ring-2 ring-indigo-400 shadow-sm'
          : isActive
            ? 'bg-indigo-50/70 ring-1 ring-indigo-200'
            : 'hover:bg-zinc-50'
      }`}
      onClick={() => {
        onClick(fieldKey);
        setIsEditing(true);
      }}
      onMouseEnter={() => onHover(fieldKey)}
      onMouseLeave={() => onHover(null)}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
          {label}
        </span>
        {isLocked && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded">
            🔒 Locked
          </span>
        )}
        {isWrong && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 rounded"
            title={wrongReason || 'Pre-populated value was incorrect'}
          >
            <AlertTriangle size={10} />
            Wrong
          </span>
        )}
        {isAiSuggestion && !isWrong && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-600 rounded">
            <Sparkles size={10} />
            AI
          </span>
        )}
        <SourceBadge source={source} />
      </div>
      {isWrong && wrongReason && (
        <div className="mb-1 text-[10px] text-red-500 italic">
          {wrongReason}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span
          className={`text-sm ${value ? 'text-zinc-900' : isWrong ? 'text-red-400 italic' : 'text-zinc-400 italic'}`}
        >
          {isWrong ? 'Needs correction' : value || placeholder}
        </span>
        <Edit3
          size={14}
          className="text-zinc-300 group-hover:text-zinc-500 transition-colors"
        />
      </div>
    </button>
  );
};

// =============================================================================
// Editable Select Field Component (for dropdowns like License Plate State)
// =============================================================================

interface EditableSelectFieldProps {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
  options: { value: string; label: string }[];
  source?: SourceReference | null;
  isAiSuggestion?: boolean;
  isWrong?: boolean;
  wrongReason?: string;
  fieldKey: string;
  isActive: boolean;
  isLocked?: boolean;
  onHover: (key: string | null) => void;
  onClick: (key: string) => void;
}

const EditableSelectField: React.FC<EditableSelectFieldProps> = ({
  label,
  value,
  placeholder = 'Not set',
  onSave,
  options,
  source,
  isAiSuggestion = false,
  isWrong = false,
  wrongReason,
  fieldKey,
  isActive,
  isLocked = false,
  onHover,
  onClick,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value || '');
    }
  }, [value, isEditing]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setIsEditing(false);
    } catch (e: unknown) {
      let errorMessage = 'Failed to save';
      if (e && typeof e === 'object') {
        const rtkError = e as { data?: { error?: string }; message?: string };
        if (rtkError.data?.error) {
          errorMessage = rtkError.data.error;
        } else if (rtkError.message) {
          errorMessage = rtkError.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value || '');
    setError(null);
    setIsEditing(false);
  };

  const displayLabel = options.find((o) => o.value === value)?.label || value;

  if (isEditing) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className="group"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => onHover(fieldKey)}
        onMouseLeave={() => onHover(null)}
      >
        <span className="block text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-1.5">
          {label}
        </span>
        <select
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-zinc-400 transition-all"
          disabled={isSaving}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1 text-xs font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="px-3 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`group cursor-pointer text-left w-full rounded-lg p-2 transition-all ${
        isLocked
          ? 'bg-indigo-100 ring-2 ring-indigo-400 shadow-sm'
          : isActive
            ? 'bg-indigo-50/70 ring-1 ring-indigo-200'
            : 'hover:bg-zinc-50'
      }`}
      onClick={() => {
        onClick(fieldKey);
        setIsEditing(true);
      }}
      onMouseEnter={() => onHover(fieldKey)}
      onMouseLeave={() => onHover(null)}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
          {label}
        </span>
        {isLocked && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded">
            Locked
          </span>
        )}
        {isWrong && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-600 rounded"
            title={wrongReason || 'Pre-populated value was incorrect'}
          >
            <AlertTriangle size={10} />
            Wrong
          </span>
        )}
        {isAiSuggestion && !isWrong && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-600 rounded">
            <Sparkles size={10} />
            AI
          </span>
        )}
        <SourceBadge source={source} />
      </div>
      {isWrong && wrongReason && (
        <div className="mb-1 text-[10px] text-red-500 italic">
          {wrongReason}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span
          className={`text-sm ${displayLabel ? 'text-zinc-900' : isWrong ? 'text-red-400 italic' : 'text-zinc-400 italic'}`}
        >
          {isWrong ? 'Needs correction' : displayLabel || placeholder}
        </span>
        <Edit3
          size={14}
          className="text-zinc-300 group-hover:text-zinc-500 transition-colors"
        />
      </div>
    </button>
  );
};

/** State options for the license plate state dropdown */
const LICENSE_PLATE_STATE_OPTIONS = [
  ...US_STATE_OPTIONS.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` })),
  { value: 'OTHER', label: 'Other' },
];

// =============================================================================
// Section Card Component
// =============================================================================

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  accent?: 'blue' | 'green' | 'orange' | 'purple';
}

const Section: React.FC<SectionProps> = ({
  icon,
  title,
  children,
  accent = 'blue',
}) => {
  const accentColors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-zinc-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-3">
        <div className={`p-2 rounded-xl ${accentColors[accent]}`}>{icon}</div>
        <h3 className="font-semibold text-zinc-900">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
};

// =============================================================================
// Vehicle Helpers
// =============================================================================

function findMatchingAiVehicle(
  dbVehicle: ReadVehicle,
  dbVehicleIndex: number,
  aiVehicles: OpenClaimContextSchema['client_vehicles'] | undefined,
): OpenClaimContextSchema['client_vehicles'][number] | undefined {
  if (!aiVehicles || aiVehicles.length === 0) return undefined;

  // Priority 1: Match by VIN
  if (dbVehicle.vin) {
    const vinMatch = aiVehicles.find(
      (ai) =>
        ai.vin?.value &&
        ai.vin.value.toLowerCase() === dbVehicle.vin?.toLowerCase(),
    );
    if (vinMatch) return vinMatch;
  }

  // Priority 2: Match by Year + Make + Model
  if (dbVehicle.year && dbVehicle.make && dbVehicle.model) {
    const ymmMatch = aiVehicles.find(
      (ai) =>
        ai.year?.value === dbVehicle.year &&
        ai.make?.value?.toLowerCase() === dbVehicle.make?.toLowerCase() &&
        ai.model?.value?.toLowerCase() === dbVehicle.model?.toLowerCase(),
    );
    if (ymmMatch) return ymmMatch;
  }

  // Priority 3: Positional match
  if (dbVehicleIndex < aiVehicles.length) {
    return aiVehicles[dbVehicleIndex];
  }

  return undefined;
}

// =============================================================================
// Vehicle Pill Component
// =============================================================================

interface VehiclePillProps {
  vehicle: ReadVehicle;
  coverages: CoverageVehicleSummary[];
  assignedCoverageId: string | null;
  onAssign: (coverageId: string | null) => void;
  onUpdateVehicle: (
    vehicleId: string,
    field: keyof UpdateVehicle,
    value: string | number | null,
  ) => Promise<void>;
  openClaimVehicle?: OpenClaimContextSchema['client_vehicles'][number];
  fieldSourcesMap: Record<string, FieldSourceInfo>;
  activeFieldKey: string | null;
  /** Function to check if a field is locked */
  isFieldLocked: (key: string) => boolean;
  onHover: (key: string | null) => void;
  onClick: (key: string) => void;
  vehicleIndex: number;
  /** Role prefix for field keys: "client" (default) or "defendant" */
  vehicleRole?: 'client' | 'defendant';
}

const VehiclePill: React.FC<VehiclePillProps> = ({
  vehicle,
  coverages,
  assignedCoverageId,
  onAssign,
  onUpdateVehicle,
  openClaimVehicle,
  fieldSourcesMap,
  activeFieldKey,
  isFieldLocked,
  onHover,
  onClick,
  vehicleIndex,
  vehicleRole = 'client',
}) => {
  const [expanded, setExpanded] = useState(false);
  const assignedCoverage = coverages.find(
    (c) => c.coverage_id === assignedCoverageId,
  );

  const getFieldKey = (field: string) =>
    `vehicle:${vehicleRole}:${vehicleIndex}:${field}`;

  // Build display title using AI data when available, falling back to DB data.
  // Skip AI values flagged as WRONG to stay consistent with editable fields.
  const displayYear = (openClaimVehicle?.year?.status !== 'WRONG' ? openClaimVehicle?.year?.value : null) ?? vehicle.year;
  const displayMake = (openClaimVehicle?.make?.status !== 'WRONG' ? openClaimVehicle?.make?.value : null) ?? vehicle.make;
  const displayModel = (openClaimVehicle?.model?.status !== 'WRONG' ? openClaimVehicle?.model?.value : null) ?? vehicle.model;
  const displayColor = (openClaimVehicle?.color?.status !== 'WRONG' ? openClaimVehicle?.color?.value : null) ?? vehicle.color;
  const displayTitle = [displayYear, displayMake, displayModel]
    .filter(Boolean)
    .map(String)
    .join(' ') || 'Unknown Vehicle';

  return (
    <div className="bg-zinc-50 rounded-xl p-4 mb-3 last:mb-0">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-sm text-zinc-900">
            {displayTitle}
          </div>
          {displayColor && (
            <div className="text-xs text-zinc-500 mt-0.5">{displayColor}</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
            assignedCoverage
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'
          }`}
        >
          {assignedCoverage
            ? `${assignedCoverage.carrier_name || (assignedCoverage.category === CoverageCategory.FIRST_PARTY ? '1P' : '3P')} ✓`
            : 'Assign'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-200">
          {/* Editable vehicle fields with citations */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {(() => {
              const isWrong = openClaimVehicle?.year?.status === 'WRONG';
              const aiYear = !isWrong ? openClaimVehicle?.year?.value : null;
              const dbYear = vehicle.year ? String(vehicle.year) : null;
              const isAi = openClaimVehicle?.year?.value != null;
              const fieldKey = getFieldKey('year');
              return (
                <EditableField
                  label="Year"
                  value={isAi && !isWrong ? String(aiYear) : dbYear}
                  onSave={(v) =>
                    onUpdateVehicle(
                      vehicle.id,
                      'year',
                      v ? parseInt(v, 10) : null,
                    )
                  }
                  isAiSuggestion={isAi}
                  isWrong={isWrong}
                  wrongReason={isWrong ? (openClaimVehicle?.year?.reasoning ?? 'Pre-populated value was incorrect') : undefined}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                  source={
                    fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null
                  }
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.make,
                vehicle.make,
              );
              const fieldKey = getFieldKey('make');
              return (
                <EditableField
                  label="Make"
                  value={merged.value}
                  onSave={(v) => onUpdateVehicle(vehicle.id, 'make', v || null)}
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.model,
                vehicle.model,
              );
              const fieldKey = getFieldKey('model');
              return (
                <EditableField
                  label="Model"
                  value={merged.value}
                  onSave={(v) =>
                    onUpdateVehicle(vehicle.id, 'model', v || null)
                  }
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.color,
                vehicle.color,
              );
              const fieldKey = getFieldKey('color');
              return (
                <EditableField
                  label="Color"
                  value={merged.value}
                  onSave={(v) =>
                    onUpdateVehicle(vehicle.id, 'color', v || null)
                  }
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.vin,
                vehicle.vin,
              );
              const fieldKey = getFieldKey('vin');
              return (
                <EditableField
                  label="VIN"
                  value={merged.value}
                  onSave={(v) => onUpdateVehicle(vehicle.id, 'vin', v || null)}
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.license_plate,
                vehicle.license_plate,
              );
              const fieldKey = getFieldKey('plate');
              return (
                <EditableField
                  label="License Plate"
                  value={merged.value}
                  onSave={(v) =>
                    onUpdateVehicle(vehicle.id, 'license_plate', v || null)
                  }
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
            {(() => {
              const merged = getMergedStringValue(
                openClaimVehicle?.license_plate_state,
                vehicle.license_plate_state_code,
              );
              const fieldKey = getFieldKey('plate_state');
              return (
                <EditableSelectField
                  label="License Plate State"
                  value={merged.value}
                  placeholder="Select state"
                  options={LICENSE_PLATE_STATE_OPTIONS}
                  onSave={(v) =>
                    onUpdateVehicle(vehicle.id, 'license_plate_state_code', v || null)
                  }
                  source={
                    merged.source ??
                    (fieldSourcesMap[fieldKey]
                      ? {
                          source_type: fieldSourcesMap[fieldKey].sourceType,
                          source_label: fieldSourcesMap[fieldKey].sourceLabel,
                          artifact_id: fieldSourcesMap[fieldKey].artifactId,
                          blob_id: null,
                        }
                      : null)
                  }
                  isAiSuggestion={merged.isAiSuggestion}
                  fieldKey={fieldKey}
                  isActive={activeFieldKey === fieldKey}
                  isLocked={isFieldLocked(fieldKey)}
                  onHover={onHover}
                  onClick={onClick}
                />
              );
            })()}
          </div>

          {/* Coverage assignment buttons */}
          <div className="pt-3 border-t border-zinc-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-zinc-500">Assign to coverage:</span>
              {openClaimVehicle?.suggested_coverage_id?.value && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-600 rounded">
                  <Sparkles size={10} />
                  AI suggested
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {coverages.map((cov) => {
                const isSelected = cov.coverage_id === assignedCoverageId;
                const isAiSuggested =
                  openClaimVehicle?.suggested_coverage_id?.value ===
                  cov.coverage_id;
                const coverageType =
                  cov.category === CoverageCategory.FIRST_PARTY ? '1P' : '3P';
                const label = cov.carrier_name
                  ? `${cov.carrier_name} (${coverageType})`
                  : `${coverageType} Coverage`;
                return (
                  <button
                    type="button"
                    key={cov.coverage_id}
                    onClick={() =>
                      onAssign(isSelected ? null : cov.coverage_id)
                    }
                    className={`flex-1 py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                      isSelected
                        ? 'bg-zinc-900 text-white'
                        : isAiSuggested
                          ? 'bg-violet-50 border-2 border-violet-300 text-violet-700 hover:border-violet-400'
                          : 'bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-400'
                    }`}
                  >
                    {isSelected && <Check size={12} className="inline mr-1" />}
                    {isAiSuggested && !isSelected && (
                      <Sparkles size={12} className="inline mr-1" />
                    )}
                    {label}
                  </button>
                );
              })}
              {assignedCoverageId && (
                <button
                  type="button"
                  onClick={() => onAssign(null)}
                  className="py-2 px-3 text-xs font-medium text-zinc-500 hover:text-zinc-700"
                >
                  Clear Assignment
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const ReviewFieldsPanel: React.FC<ReviewFieldsPanelProps> = ({
  matterId,
  coverageId,
  coverage: linkedCoverage,
  activeFieldKey,
  lockedFieldKey,
  onActiveFieldKeyChange,
  onFieldClick,
  onActiveFieldSourceChange,
}) => {
  const { data: bpoContext, refetch: refetchBpoContext } = useGetBpoContextQuery(
    { matterId },
    { skip: !matterId },
  );

  const {
    data: fetchedCoverage,
    refetch: refetchCoverageRaw,
    isUninitialized: isCoverageQuerySkipped,
  } = useGetCoverageQuery(
    { matterId, coverageId },
    { skip: !matterId || !coverageId || !!linkedCoverage },
  );

  const refetchCoverage = useCallback(async () => {
    if (!isCoverageQuerySkipped) {
      await refetchCoverageRaw();
    }
  }, [isCoverageQuerySkipped, refetchCoverageRaw]);

  const coverage = linkedCoverage ?? fetchedCoverage;
  const openClaimContext = coverage?.open_claim_context;

  const [updateBpoContext] = useUpdateBpoContextMutation();
  const [updateVehicle] = useVehiclesUpdateMutation();

  // Build field sources map for citation tracking
  const fieldSourcesMap = useMemo(() => {
    const map: Record<string, FieldSourceInfo> = {};

    const extractArtifactIdFromCitations = (
      citationIds: string[] | undefined,
    ): string | null => {
      if (!citationIds || citationIds.length === 0) return null;
      const firstCitation = citationIds[0];
      const uuidColonMatch = firstCitation.match(
        /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):/i,
      );
      if (uuidColonMatch) return uuidColonMatch[1];
      const numMatch = firstCitation.match(/^(\d+)\/page\//);
      return numMatch ? numMatch[1] : null;
    };

    const normalizeCitationIds = (
      citationIds: string[] | undefined,
    ): string[] => {
      if (!citationIds) return [];
      return citationIds.map((cid) => {
        const uuidColonMatch = cid.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:(.+)$/i,
        );
        if (uuidColonMatch) return uuidColonMatch[1];
        const numMatch = cid.match(/^\d+(\/page\/.+)$/);
        if (numMatch) return numMatch[1];
        return cid;
      });
    };

    const addSource = (
      key: string,
      openClaimField: OpenClaimCitedField<unknown> | undefined,
      bpoField: CitedField<unknown> | null | undefined,
    ) => {
      const artifactId =
        openClaimField?.source_artifact_id ||
        extractArtifactIdFromCitations(openClaimField?.citation_ids);

      if (artifactId) {
        map[key] = {
          artifactId,
          citationIds: normalizeCitationIds(openClaimField?.citation_ids),
          sourceType: SourceType.COVERAGE_DOC,
          sourceLabel: openClaimField?.source_artifact_id
            ? 'AI Suggested'
            : 'AI Discovered',
          isAiSuggestion: true,
        };
        return;
      }

      const merged = getMergedValue(openClaimField, bpoField);
      if (merged.source?.artifact_id) {
        map[key] = {
          artifactId: merged.source.artifact_id,
          citationIds: [],
          sourceType: merged.source.source_type,
          sourceLabel: merged.source.source_label,
          isAiSuggestion: merged.isAiSuggestion,
        };
      }
    };

    // Client fields
    addSource(
      'client:name',
      openClaimContext?.client?.name,
      bpoContext?.client_name,
    );
    addSource(
      'client:phone',
      openClaimContext?.client?.phone,
      bpoContext?.client_phone,
    );
    addSource(
      'client:email',
      openClaimContext?.client?.email,
      bpoContext?.client_email,
    );
    addSource(
      'client:address',
      openClaimContext?.client?.address,
      bpoContext?.client_address,
    );
    addSource(
      'client:city',
      openClaimContext?.client?.city,
      bpoContext?.client_city,
    );
    addSource(
      'client:state',
      openClaimContext?.client?.state,
      bpoContext?.client_state,
    );
    addSource(
      'client:zip',
      openClaimContext?.client?.zip_code,
      bpoContext?.client_zip,
    );
    addSource(
      'client:dob',
      openClaimContext?.client?.dob,
      bpoContext?.client_dob,
    );
    addSource(
      'client:ssn',
      openClaimContext?.client?.last_four_ssn,
      bpoContext?.client_last_four_ssn,
    );

    // Incident fields
    addSource(
      'incident:date',
      openClaimContext?.incident?.date_of_loss,
      bpoContext?.date_of_loss,
    );
    addSource(
      'incident:time',
      openClaimContext?.incident?.time_of_loss,
      bpoContext?.time_of_loss,
    );
    addSource(
      'incident:location',
      openClaimContext?.incident?.location,
      bpoContext?.location,
    );
    addSource(
      'incident:weather',
      openClaimContext?.incident?.weather_conditions,
      bpoContext?.weather_conditions,
    );
    addSource(
      'incident:description',
      openClaimContext?.incident?.incident_description,
      bpoContext?.incident_description,
    );
    addSource(
      'incident:matter_city',
      openClaimContext?.matter_city,
      bpoContext?.matter_city,
    );
    addSource(
      'incident:matter_state',
      openClaimContext?.matter_state,
      bpoContext?.matter_state,
    );

    // Insurance fields
    const currentCoverageSummary = bpoContext?.coverages?.find(
      (c) => c.coverage_id === coverageId,
    );
    const makeBpoField = (
      value: string | null | undefined,
    ): CitedField<string | null> | null =>
      currentCoverageSummary
        ? { value: value ?? null, source: currentCoverageSummary.source }
        : null;

    addSource(
      'ins:carrier',
      openClaimContext?.coverage?.carrier_name,
      makeBpoField(currentCoverageSummary?.carrier_name),
    );
    addSource(
      'ins:policy',
      openClaimContext?.coverage?.policy_number,
      makeBpoField(currentCoverageSummary?.policy_number),
    );
    addSource(
      'ins:insured',
      openClaimContext?.coverage?.named_insured,
      makeBpoField(currentCoverageSummary?.named_insured),
    );
    addSource('ins:phone', openClaimContext?.coverage?.carrier_phone, null);
    addSource(
      'ins:claim',
      openClaimContext?.coverage?.claim_number,
      makeBpoField(currentCoverageSummary?.claim_number),
    );

    // At-fault driver fields
    const atFault = openClaimContext?.at_fault_driver;
    if (atFault) {
      const addAtFaultSource = (
        key: string,
        field: OpenClaimCitedField<string> | undefined,
      ) => {
        const artifactId =
          field?.source_artifact_id ||
          extractArtifactIdFromCitations(field?.citation_ids);
        if (artifactId) {
          map[key] = {
            artifactId,
            citationIds: normalizeCitationIds(field?.citation_ids),
            sourceType: SourceType.POLICE_REPORT,
            sourceLabel: 'AI Discovered',
            isAiSuggestion: true,
          };
        }
      };
      addAtFaultSource('at_fault:name', atFault.name);
      addAtFaultSource('at_fault:phone', atFault.phone);
      addAtFaultSource('at_fault:address', atFault.address);
    }

    // Client vehicles sources
    openClaimContext?.client_vehicles?.forEach((vehicle, idx) => {
      const addClientVehicleSource = (
        field: OpenClaimCitedField<unknown> | undefined,
        fieldName: string,
      ) => {
        const artifactId =
          field?.source_artifact_id ||
          extractArtifactIdFromCitations(field?.citation_ids);
        if (artifactId) {
          map[`vehicle:client:${idx}:${fieldName}`] = {
            artifactId,
            citationIds: normalizeCitationIds(field?.citation_ids),
            sourceType: SourceType.COVERAGE_DOC,
            sourceLabel: 'AI Discovered',
            isAiSuggestion: true,
          };
        }
      };
      addClientVehicleSource(vehicle.year, 'year');
      addClientVehicleSource(vehicle.make, 'make');
      addClientVehicleSource(vehicle.model, 'model');
      addClientVehicleSource(vehicle.color, 'color');
      addClientVehicleSource(vehicle.vin, 'vin');
      addClientVehicleSource(vehicle.license_plate, 'plate');
      addClientVehicleSource(vehicle.license_plate_state, 'plate_state');
    });

    return map;
  }, [openClaimContext, bpoContext, coverageId]);

  // Report active field source changes
  useEffect(() => {
    if (activeFieldKey && fieldSourcesMap[activeFieldKey]) {
      onActiveFieldSourceChange(fieldSourcesMap[activeFieldKey]);
    } else {
      onActiveFieldSourceChange(null);
    }
  }, [activeFieldKey, fieldSourcesMap, onActiveFieldSourceChange]);

  // Handlers
  const handleHover = useCallback(
    (key: string | null) => {
      if (key !== activeFieldKey) {
        onActiveFieldKeyChange(key);
      }
    },
    [activeFieldKey, onActiveFieldKeyChange],
  );

  const handleClick = useCallback(
    (key: string) => {
      if (onFieldClick) {
        onFieldClick(key);
      } else {
        onActiveFieldKeyChange(key);
      }
    },
    [onActiveFieldKeyChange, onFieldClick],
  );

  // Determine if a field should show as "locked" (visually selected)
  const isFieldLocked = useCallback(
    (key: string) => lockedFieldKey === key,
    [lockedFieldKey],
  );

  // Save field to BPO context
  const saveField = useCallback(
    async <K extends keyof UpdateBPOContextRequest>(
      field: K,
      value: UpdateBPOContextRequest[K],
    ): Promise<void> => {
      const updates: UpdateBPOContextRequest = {
        coverage_id: coverageId,
        [field]: value,
      };
      await updateBpoContext({ matterId, updates }).unwrap();
      // Refetch both BPO context and coverage (to clear open_claim_context AI suggestions)
      await Promise.all([refetchBpoContext(), refetchCoverage()]);
    },
    [matterId, coverageId, updateBpoContext, refetchBpoContext, refetchCoverage],
  );

  // =============================================================================
  // Vehicle State & Handlers
  // =============================================================================
  const [assignVehicles] = useAssignVehiclesToCoverageMutation();
  const [createVehicle, { isLoading: isCreatingVehicle }] =
    useVehiclesCreateMutation();

  // Local state for vehicle assignments
  const [vehicleAssignments, setVehicleAssignments] = useState<
    Record<string, string | null>
  >({});

  // Add vehicle form state
  const [isAddingVehicle, setIsAddingVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState<{
    year: string;
    make: string;
    model: string;
    color: string;
    vin: string;
    license_plate: string;
    license_plate_state_code: string;
  }>({
    year: '',
    make: '',
    model: '',
    color: '',
    vin: '',
    license_plate: '',
    license_plate_state_code: '',
  });

  // Initialize vehicle assignments from context
  useEffect(() => {
    if (bpoContext) {
      const initial: Record<string, string | null> = {};
      bpoContext.all_vehicles.forEach((va) => {
        initial[va.vehicle.id] = va.assigned_coverage_id;
      });
      setVehicleAssignments(initial);
    }
  }, [bpoContext]);

  // Save vehicle assignment immediately when user clicks assign
  const handleVehicleAssign = useCallback(
    async (vehicleId: string, newCoverageId: string | null) => {
      if (!bpoContext) return;

      const oldCoverageId = vehicleAssignments[vehicleId];

      // Update local state immediately for responsiveness
      setVehicleAssignments((prev) => ({
        ...prev,
        [vehicleId]: newCoverageId,
      }));

      try {
        const affectedCoverageIds = new Set<string>();
        if (oldCoverageId) affectedCoverageIds.add(oldCoverageId);
        if (newCoverageId) affectedCoverageIds.add(newCoverageId);

        const newAssignments = {
          ...vehicleAssignments,
          [vehicleId]: newCoverageId,
        };

        const promises = Array.from(affectedCoverageIds).map((covId) => {
          const vehicleIdsForCoverage = Object.entries(newAssignments)
            .filter(([, assignedCovId]) => assignedCovId === covId)
            .map(([vId]) => vId);

          return assignVehicles({
            matterId,
            coverageId: covId,
            vehicleIds: vehicleIdsForCoverage,
          });
        });

        await Promise.all(promises);
        await Promise.all([refetchBpoContext(), refetchCoverage()]);
      } catch (error) {
        console.error('Failed to assign vehicle:', error);
        setVehicleAssignments((prev) => ({
          ...prev,
          [vehicleId]: oldCoverageId,
        }));
      }
    },
    [bpoContext, vehicleAssignments, matterId, assignVehicles, refetchBpoContext, refetchCoverage],
  );

  // Update a vehicle field and re-assign to coverage if assigned
  const handleUpdateVehicle = useCallback(
    async (
      vehicleId: string,
      field: keyof UpdateVehicle,
      value: string | number | null,
    ) => {
      try {
        await updateVehicle({
          matterId,
          id: vehicleId,
          patchedUpdateVehicle: { id: vehicleId, [field]: value },
        }).unwrap();

        const assignedCovId = vehicleAssignments[vehicleId];
        if (assignedCovId) {
          const vehicleIdsForCoverage = Object.entries(vehicleAssignments)
            .filter(([, covId]) => covId === assignedCovId)
            .map(([vId]) => vId);

          await assignVehicles({
            matterId,
            coverageId: assignedCovId,
            vehicleIds: vehicleIdsForCoverage,
          });
        }

        await Promise.all([refetchBpoContext(), refetchCoverage()]);
      } catch (error) {
        console.error('Failed to update vehicle:', error);
        throw error;
      }
    },
    [matterId, vehicleAssignments, updateVehicle, assignVehicles, refetchBpoContext, refetchCoverage],
  );

  // Create a new vehicle
  const handleCreateVehicle = useCallback(async () => {
    try {
      const vehicleData: CreateVehicle = {
        matter_id: matterId,
        vehicle_type: 'passenger_car' as VehicleType,
        year: newVehicle.year ? parseInt(newVehicle.year, 10) : null,
        make: newVehicle.make || null,
        model: newVehicle.model || null,
        color: newVehicle.color || null,
        vin: newVehicle.vin || null,
        license_plate: newVehicle.license_plate || null,
        license_plate_state_code: newVehicle.license_plate_state_code || null,
      };

      await createVehicle({
        matterId,
        createVehicle: vehicleData,
      }).unwrap();

      setNewVehicle({
        year: '',
        make: '',
        model: '',
        color: '',
        vin: '',
        license_plate: '',
        license_plate_state_code: '',
      });
      setIsAddingVehicle(false);
      await Promise.all([refetchBpoContext(), refetchCoverage()]);
    } catch (error) {
      console.error('Failed to create vehicle:', error);
    }
  }, [matterId, newVehicle, createVehicle, refetchBpoContext, refetchCoverage]);

  // Get merged values
  const clientName = getMergedValue(
    openClaimContext?.client?.name,
    bpoContext?.client_name,
  );
  const clientPhone = getMergedValue(
    openClaimContext?.client?.phone,
    bpoContext?.client_phone,
  );
  const clientEmail = getMergedValue(
    openClaimContext?.client?.email,
    bpoContext?.client_email,
  );
  const clientAddress = getMergedValue(
    openClaimContext?.client?.address,
    bpoContext?.client_address,
  );
  const clientCity = getMergedValue(
    openClaimContext?.client?.city,
    bpoContext?.client_city,
  );
  const clientState = getMergedValue(
    openClaimContext?.client?.state,
    bpoContext?.client_state,
  );
  const clientZip = getMergedValue(
    openClaimContext?.client?.zip_code,
    bpoContext?.client_zip,
  );
  const clientDob = getMergedValue(
    openClaimContext?.client?.dob,
    bpoContext?.client_dob,
  );
  const clientSsn = getMergedValue(
    openClaimContext?.client?.last_four_ssn,
    bpoContext?.client_last_four_ssn,
  );

  const incidentDate = getMergedValue(
    openClaimContext?.incident?.date_of_loss,
    bpoContext?.date_of_loss,
  );
  const incidentTime = getMergedValue(
    openClaimContext?.incident?.time_of_loss,
    bpoContext?.time_of_loss,
  );
  const incidentLocation = getMergedValue(
    openClaimContext?.incident?.location,
    bpoContext?.location,
  );
  const incidentWeather = getMergedValue(
    openClaimContext?.incident?.weather_conditions,
    bpoContext?.weather_conditions,
  );
  const incidentDescription = getMergedValue(
    openClaimContext?.incident?.incident_description,
    bpoContext?.incident_description,
  );
  const matterCity = getMergedValue(
    openClaimContext?.matter_city,
    bpoContext?.matter_city,
  );
  const matterState = getMergedValue(
    openClaimContext?.matter_state,
    bpoContext?.matter_state,
  );

  const currentCoverageSummary= bpoContext?.coverages?.find(
    (c) => c.coverage_id === coverageId,
  );
  const carrierName = getMergedValue(
    openClaimContext?.coverage?.carrier_name,
    currentCoverageSummary
      ? {
          value: currentCoverageSummary.carrier_name,
          source: currentCoverageSummary.source,
        }
      : null,
  );
  const policyNumber = getMergedValue(
    openClaimContext?.coverage?.policy_number,
    currentCoverageSummary
      ? {
          value: currentCoverageSummary.policy_number,
          source: currentCoverageSummary.source,
        }
      : null,
  );
  const namedInsured = getMergedValue(
    openClaimContext?.coverage?.named_insured,
    currentCoverageSummary
      ? {
          value: currentCoverageSummary.named_insured,
          source: currentCoverageSummary.source,
        }
      : null,
  );
  const claimNumber = getMergedValue(
    openClaimContext?.coverage?.claim_number,
    currentCoverageSummary
      ? {
          value: currentCoverageSummary.claim_number,
          source: currentCoverageSummary.source,
        }
      : null,
  );

  const atFaultName = getMergedStringValue(
    openClaimContext?.at_fault_driver?.name,
    bpoContext?.at_fault_driver?.name,
  );
  const atFaultPhone = getMergedStringValue(
    openClaimContext?.at_fault_driver?.phone,
    bpoContext?.at_fault_driver?.phone,
  );
  const atFaultAddress = getMergedStringValue(
    openClaimContext?.at_fault_driver?.address,
    bpoContext?.at_fault_driver?.address,
  );

  return (
    <div className="space-y-5 overflow-y-auto h-full p-6 bg-zinc-50">
      {/* Client Section */}
      <Section icon={<User size={18} />} title="Client" accent="blue">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          <EditableField
            label="Name"
            value={clientName.value}
            onSave={(v) => saveField('client_name', v)}
            source={clientName.source}
            isAiSuggestion={clientName.isAiSuggestion}
            isWrong={clientName.isWrong}
            wrongReason={clientName.wrongReason}
            fieldKey="client:name"
            isActive={activeFieldKey === 'client:name'}
            isLocked={isFieldLocked('client:name')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Phone"
            value={clientPhone.value}
            onSave={(v) => saveField('client_phone', v)}
            source={clientPhone.source}
            isAiSuggestion={clientPhone.isAiSuggestion}
            isWrong={clientPhone.isWrong}
            wrongReason={clientPhone.wrongReason}
            fieldKey="client:phone"
            isActive={activeFieldKey === 'client:phone'}
            isLocked={isFieldLocked('client:phone')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Email"
            value={clientEmail.value}
            onSave={(v) => saveField('client_email', v)}
            source={clientEmail.source}
            isAiSuggestion={clientEmail.isAiSuggestion}
            isWrong={clientEmail.isWrong}
            wrongReason={clientEmail.wrongReason}
            fieldKey="client:email"
            isActive={activeFieldKey === 'client:email'}
            isLocked={isFieldLocked('client:email')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Date of Birth"
            value={clientDob.value}
            onSave={(v) => saveField('client_dob', v)}
            source={clientDob.source}
            isAiSuggestion={clientDob.isAiSuggestion}
            isWrong={clientDob.isWrong}
            wrongReason={clientDob.wrongReason}
            fieldKey="client:dob"
            isActive={activeFieldKey === 'client:dob'}
            isLocked={isFieldLocked('client:dob')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Last 4 SSN"
            value={clientSsn.value}
            onSave={(v) => saveField('client_last_four_ssn', v)}
            source={clientSsn.source}
            isAiSuggestion={clientSsn.isAiSuggestion}
            isWrong={clientSsn.isWrong}
            wrongReason={clientSsn.wrongReason}
            fieldKey="client:ssn"
            isActive={activeFieldKey === 'client:ssn'}
            isLocked={isFieldLocked('client:ssn')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Address"
            value={clientAddress.value}
            onSave={(v) => saveField('client_address', v)}
            source={clientAddress.source}
            isAiSuggestion={clientAddress.isAiSuggestion}
            isWrong={clientAddress.isWrong}
            wrongReason={clientAddress.wrongReason}
            fieldKey="client:address"
            isActive={activeFieldKey === 'client:address'}
            isLocked={isFieldLocked('client:address')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="City"
            value={clientCity.value}
            onSave={(v) => saveField('client_city', v)}
            source={clientCity.source}
            isAiSuggestion={clientCity.isAiSuggestion}
            isWrong={clientCity.isWrong}
            wrongReason={clientCity.wrongReason}
            fieldKey="client:city"
            isActive={activeFieldKey === 'client:city'}
            isLocked={isFieldLocked('client:city')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="State"
            value={clientState.value}
            onSave={(v) => saveField('client_state', v)}
            source={clientState.source}
            isAiSuggestion={clientState.isAiSuggestion}
            isWrong={clientState.isWrong}
            wrongReason={clientState.wrongReason}
            fieldKey="client:state"
            isActive={activeFieldKey === 'client:state'}
            isLocked={isFieldLocked('client:state')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="ZIP Code"
            value={clientZip.value}
            onSave={(v) => saveField('client_zip', v)}
            source={clientZip.source}
            isAiSuggestion={clientZip.isAiSuggestion}
            fieldKey="client:zip"
            isActive={activeFieldKey === 'client:zip'}
            isLocked={isFieldLocked('client:zip')}
            onHover={handleHover}
            onClick={handleClick}
          />
        </div>
      </Section>

      {/* Incident Section */}
      <Section
        icon={<ChevronRight size={18} />}
        title="Incident"
        accent="orange"
      >
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <EditableField
              label="Date of Loss"
              value={incidentDate.value}
              onSave={(v) => saveField('date_of_loss', v)}
              source={incidentDate.source}
              isAiSuggestion={incidentDate.isAiSuggestion}
              fieldKey="incident:date"
              isActive={activeFieldKey === 'incident:date'}
              isLocked={isFieldLocked('incident:date')}
              onHover={handleHover}
              onClick={handleClick}
            />
            <EditableField
              label="Time of Loss"
              value={incidentTime.value}
              onSave={(v) => saveField('time_of_loss', v)}
              source={incidentTime.source}
              isAiSuggestion={incidentTime.isAiSuggestion}
              fieldKey="incident:time"
              isActive={activeFieldKey === 'incident:time'}
              isLocked={isFieldLocked('incident:time')}
              onHover={handleHover}
              onClick={handleClick}
            />
            <EditableField
              label="Location"
              value={incidentLocation.value}
              onSave={(v) => saveField('location', v)}
              source={incidentLocation.source}
              isAiSuggestion={incidentLocation.isAiSuggestion}
              fieldKey="incident:location"
              isActive={activeFieldKey === 'incident:location'}
              isLocked={isFieldLocked('incident:location')}
              onHover={handleHover}
              onClick={handleClick}
            />
            <EditableField
              label="Weather"
              value={incidentWeather.value}
              onSave={(v) => saveField('weather_conditions', v)}
              source={incidentWeather.source}
              isAiSuggestion={incidentWeather.isAiSuggestion}
              fieldKey="incident:weather"
              isActive={activeFieldKey === 'incident:weather'}
              isLocked={isFieldLocked('incident:weather')}
              onHover={handleHover}
              onClick={handleClick}
            />
            <EditableField
              label="Matter City"
              value={matterCity.value}
              onSave={(v) => saveField('matter_city', v)}
              source={matterCity.source}
              isAiSuggestion={matterCity.isAiSuggestion}
              fieldKey="incident:matter_city"
              isActive={activeFieldKey === 'incident:matter_city'}
              isLocked={isFieldLocked('incident:matter_city')}
              onHover={handleHover}
              onClick={handleClick}
            />
            <EditableField
              label="Matter State"
              value={matterState.value}
              onSave={(v) => saveField('matter_state', v)}
              source={matterState.source}
              isAiSuggestion={matterState.isAiSuggestion}
              fieldKey="incident:matter_state"
              isActive={activeFieldKey === 'incident:matter_state'}
              isLocked={isFieldLocked('incident:matter_state')}
              onHover={handleHover}
              onClick={handleClick}
            />
          </div>
          <EditableField
            label="Description"
            value={incidentDescription.value}
            onSave={(v) => saveField('incident_description', v)}
            multiline
            source={incidentDescription.source}
            isAiSuggestion={incidentDescription.isAiSuggestion}
            fieldKey="incident:description"
            isActive={activeFieldKey === 'incident:description'}
            isLocked={isFieldLocked('incident:description')}
            onHover={handleHover}
            onClick={handleClick}
          />
        </div>
      </Section>

      {/* At-Fault Party Section */}
      <Section icon={<User size={18} />} title="At-Fault Party" accent="purple">
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          <EditableField
            label="Name"
            value={atFaultName.value}
            onSave={(v) => saveField('at_fault_driver_name', v)}
            source={atFaultName.source ?? bpoContext?.at_fault_driver?.source}
            isAiSuggestion={atFaultName.isAiSuggestion}
            fieldKey="at_fault:name"
            isActive={activeFieldKey === 'at_fault:name'}
            isLocked={isFieldLocked('at_fault:name')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Phone"
            value={atFaultPhone.value}
            onSave={(v) => saveField('at_fault_driver_phone', v)}
            source={atFaultPhone.source ?? bpoContext?.at_fault_driver?.source}
            isAiSuggestion={atFaultPhone.isAiSuggestion}
            fieldKey="at_fault:phone"
            isActive={activeFieldKey === 'at_fault:phone'}
            isLocked={isFieldLocked('at_fault:phone')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <div className="col-span-2">
            <EditableField
              label="Address"
              value={atFaultAddress.value}
              onSave={(v) => saveField('at_fault_driver_address', v)}
              source={
                atFaultAddress.source ?? bpoContext?.at_fault_driver?.source
              }
              isAiSuggestion={atFaultAddress.isAiSuggestion}
              fieldKey="at_fault:address"
              isActive={activeFieldKey === 'at_fault:address'}
              isLocked={isFieldLocked('at_fault:address')}
              onHover={handleHover}
              onClick={handleClick}
            />
          </div>
        </div>

      </Section>

      {/* Insurance Coverage Section */}
      <Section
        icon={<Shield size={18} />}
        title="Insurance Coverage"
        accent="green"
      >
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          <EditableField
            label="Insurance Carrier"
            value={carrierName.value}
            placeholder="e.g., State Farm"
            onSave={(v) => saveField('carrier_name', v)}
            source={carrierName.source}
            isAiSuggestion={carrierName.isAiSuggestion}
            isWrong={carrierName.isWrong}
            wrongReason={carrierName.wrongReason}
            fieldKey="ins:carrier"
            isActive={activeFieldKey === 'ins:carrier'}
            isLocked={isFieldLocked('ins:carrier')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Policy Number"
            value={policyNumber.value}
            placeholder="Policy #"
            onSave={(v) => saveField('policy_number', v)}
            source={policyNumber.source}
            isAiSuggestion={policyNumber.isAiSuggestion}
            isWrong={policyNumber.isWrong}
            wrongReason={policyNumber.wrongReason}
            fieldKey="ins:policy"
            isActive={activeFieldKey === 'ins:policy'}
            isLocked={isFieldLocked('ins:policy')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Named Insured"
            value={namedInsured.value}
            placeholder="Policyholder name"
            onSave={(v) => saveField('named_insured', v)}
            source={namedInsured.source}
            isAiSuggestion={namedInsured.isAiSuggestion}
            isWrong={namedInsured.isWrong}
            wrongReason={namedInsured.wrongReason}
            fieldKey="ins:insured"
            isActive={activeFieldKey === 'ins:insured'}
            isLocked={isFieldLocked('ins:insured')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Carrier Phone"
            value={openClaimContext?.coverage?.carrier_phone?.value ?? null}
            placeholder="(800) 555-1234"
            onSave={(v) => saveField('carrier_phone', v)}
            isAiSuggestion={
              !!openClaimContext?.coverage?.carrier_phone?.source_artifact_id
            }
            fieldKey="ins:phone"
            isActive={activeFieldKey === 'ins:phone'}
            isLocked={isFieldLocked('ins:phone')}
            onHover={handleHover}
            onClick={handleClick}
          />
          <EditableField
            label="Claim Number"
            value={claimNumber.value}
            placeholder="Claim #"
            onSave={(v) => saveField('claim_number', v)}
            source={claimNumber.source}
            isAiSuggestion={claimNumber.isAiSuggestion}
            isWrong={claimNumber.isWrong}
            wrongReason={claimNumber.wrongReason}
            fieldKey="ins:claim"
            isActive={activeFieldKey === 'ins:claim'}
            isLocked={isFieldLocked('ins:claim')}
            onHover={handleHover}
            onClick={handleClick}
          />
        </div>
      </Section>

      {/* Vehicles Section */}
      <Section icon={<Car size={18} />} title="Vehicles" accent="blue">
        {bpoContext?.all_vehicles && bpoContext.all_vehicles.length > 0 ? (
          bpoContext.all_vehicles.map((va, index) => (
            <VehiclePill
              key={va.vehicle.id}
              vehicle={va.vehicle}
              coverages={bpoContext.coverages}
              assignedCoverageId={vehicleAssignments[va.vehicle.id] ?? null}
              onAssign={(covId) => handleVehicleAssign(va.vehicle.id, covId)}
              onUpdateVehicle={handleUpdateVehicle}
              openClaimVehicle={findMatchingAiVehicle(
                va.vehicle,
                index,
                openClaimContext?.client_vehicles,
              )}
              fieldSourcesMap={fieldSourcesMap}
              activeFieldKey={activeFieldKey}
              isFieldLocked={isFieldLocked}
              onHover={handleHover}
              onClick={handleClick}
              vehicleIndex={index}
            />
          ))
        ) : (
          <div className="text-center py-8 text-sm text-zinc-400">
            No vehicles found
          </div>
        )}

        {/* Add Vehicle Form */}
        {isAddingVehicle ? (
          <div className="mt-3 p-4 bg-zinc-50 rounded-xl border border-zinc-200">
            <div className="text-sm font-medium text-zinc-700 mb-3">
              Add New Vehicle
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Year"
                value={newVehicle.year}
                onChange={(e) =>
                  setNewVehicle((prev) => ({ ...prev, year: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Make"
                value={newVehicle.make}
                onChange={(e) =>
                  setNewVehicle((prev) => ({ ...prev, make: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Model"
                value={newVehicle.model}
                onChange={(e) =>
                  setNewVehicle((prev) => ({ ...prev, model: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Color"
                value={newVehicle.color}
                onChange={(e) =>
                  setNewVehicle((prev) => ({ ...prev, color: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="VIN"
                value={newVehicle.vin}
                onChange={(e) =>
                  setNewVehicle((prev) => ({ ...prev, vin: e.target.value }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="License Plate"
                value={newVehicle.license_plate}
                onChange={(e) =>
                  setNewVehicle((prev) => ({
                    ...prev,
                    license_plate: e.target.value,
                  }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={newVehicle.license_plate_state_code}
                onChange={(e) =>
                  setNewVehicle((prev) => ({
                    ...prev,
                    license_plate_state_code: e.target.value,
                  }))
                }
                className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Plate State</option>
                {LICENSE_PLATE_STATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  setIsAddingVehicle(false);
                  setNewVehicle({
                    year: '',
                    make: '',
                    model: '',
                    color: '',
                    vin: '',
                    license_plate: '',
                    license_plate_state_code: '',
                  });
                }}
                className="px-3 py-1.5 text-sm text-zinc-600 hover:text-zinc-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateVehicle}
                disabled={isCreatingVehicle}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isCreatingVehicle ? 'Adding...' : 'Add Vehicle'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsAddingVehicle(true)}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-zinc-600 bg-zinc-50 hover:bg-zinc-100 rounded-xl border border-dashed border-zinc-300 transition-colors"
          >
            <Plus size={16} />
            Add Vehicle
          </button>
        )}
      </Section>

      {/* AI Discovery Summary */}
      {openClaimContext?.discovery_summary && (
        <div className="bg-violet-50/50 rounded-2xl border border-violet-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-violet-500" />
            <h3 className="font-semibold text-violet-900">
              AI Discovery Summary
            </h3>
          </div>
          <p className="text-sm text-violet-800 whitespace-pre-wrap">
            {openClaimContext.discovery_summary}
          </p>
        </div>
      )}
    </div>
  );
};
